import type { TenantMedia } from '@avrahami/contracts';
import {
  Button,
  countText,
  createFormStatus,
  FileDropCard,
  IMAGE_UPLOAD_ACCEPT,
  PlusIcon,
  SvgImageError,
  TextField,
  Thumbnail as ImageThumbnail,
  uploadableImage,
} from '@avrahami/ui';
import { useState, type ReactNode } from 'react';
import { tenantApi } from '../api/tenant';
import { he } from '../i18n/he';
import { errorMessage, mergeErrors, type FieldErrors } from '../lib/forms';
import { mediaUrl, uploadProblem } from '../lib/media';

/**
 * Form building blocks of the tenant admin. They only present and collect values; conversions
 * (shekels ↔ agorot, local time ↔ instants) live in `src/lib`.
 */

interface ValueFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: ReactNode;
  disabled?: boolean | undefined;
  name?: string;
  className?: string;
}

/** An amount in shekels (the draft keeps the text; `lib/money` turns it into agorot). */
export function MoneyField({
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  name,
  className,
}: ValueFieldProps) {
  return (
    <TextField
      label={label}
      name={name}
      value={value}
      inputMode="decimal"
      dir="ltr"
      autoComplete="off"
      error={error}
      hint={hint ?? he.money.hint}
      disabled={disabled}
      className={className}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/** A whole number (positions, quantities, limits). */
export function NumberField({
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  name,
  className,
}: ValueFieldProps) {
  return (
    <TextField
      label={label}
      name={name}
      value={value}
      inputMode="numeric"
      dir="ltr"
      autoComplete="off"
      error={error}
      hint={hint}
      disabled={disabled}
      className={className}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/** A local date and time (`lib/datetime` adds the browser's offset before sending). */
export function DateTimeField({
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  name,
  className,
}: ValueFieldProps) {
  return (
    <TextField
      label={label}
      name={name}
      type="datetime-local"
      value={value}
      error={error}
      hint={hint ?? he.common.windowHint}
      disabled={disabled}
      className={className}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/** Start and end of an active window, side by side. */
export function WindowFields({
  startsAt,
  endsAt,
  onChange,
  errors,
  disabled,
}: {
  startsAt: string;
  endsAt: string;
  onChange: (change: { startsAt?: string; endsAt?: string }) => void;
  errors: FieldErrors;
  disabled: boolean;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <DateTimeField
        label={he.common.startsAt}
        name="startsAt"
        value={startsAt}
        error={errors['startsAt']}
        disabled={disabled}
        onChange={(value) => {
          onChange({ startsAt: value });
        }}
      />
      <DateTimeField
        label={he.common.endsAt}
        name="endsAt"
        value={endsAt}
        error={errors['endsAt']}
        disabled={disabled}
        onChange={(value) => {
          onChange({ endsAt: value });
        }}
      />
    </div>
  );
}

/** A thumbnail of an image of this store (or, with `urlOf`, of an image served elsewhere). */
export function Thumbnail({
  file,
  alt,
  size = 64,
  urlOf = mediaUrl,
}: {
  file: string;
  alt: string;
  size?: number;
  urlOf?: (file: string) => string;
}) {
  return <ImageThumbnail src={urlOf(file)} alt={alt} size={size} />;
}

/**
 * Uploads images (POST /v1/tenant/media) one after another, in the order given, and hands each
 * stored media to the caller. Nothing is uploaded when a file is wrong or there are more than
 * `limit` of them. `start` answers whether all of them were stored.
 */
export function useImageUpload(onUploaded: (media: TenantMedia) => void, limit = 1) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const start = async (files: readonly File[]): Promise<boolean> => {
    setError(undefined);
    if (files.length > limit) {
      setError(countText(limit, he.upload.tooMany));
      return false;
    }
    const found = files.map(uploadProblem).find((problem) => problem !== null);
    if (found !== undefined) {
      setError(found === 'TOO_LARGE' ? he.upload.tooLarge : he.upload.notImage);
      return false;
    }
    setBusy(true);
    try {
      for (const file of files)
        onUploaded(await tenantApi.uploadMedia(await uploadableImage(file)));
      return true;
    } catch (failure) {
      setError(failure instanceof SvgImageError ? he.upload.badSvg : errorMessage(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return {
    start,
    busy,
    error,
    clear: () => {
      setError(undefined);
    },
  };
}

/**
 * The one way to add an image in this admin: a button that opens a card to drag the image onto
 * or to choose it from the computer. The card closes once the upload is stored. `limit` is how
 * many images may be added at once (the test hook is on the file input, `<testId>-open` on the
 * button).
 */
export function ImageAdder({
  label,
  onUploaded,
  limit = 1,
  testId,
}: {
  label: string;
  onUploaded: (media: TenantMedia) => void;
  limit?: number;
  testId?: string;
}) {
  const [state, setState] = useState<'initial' | 'open' | 'closed'>('initial');
  const upload = useImageUpload(onUploaded, limit);
  const close = () => {
    upload.clear();
    setState('closed');
  };
  if (state !== 'open') {
    return (
      <div>
        <Button
          tone="secondary"
          // Closing the card returns the keyboard to the button that opened it.
          autoFocus={state === 'closed'}
          data-testid={testId === undefined ? undefined : `${testId}-open`}
          onClick={() => {
            setState('open');
          }}
        >
          <PlusIcon />
          {label}
        </Button>
      </div>
    );
  }
  return (
    <FileDropCard
      title={label}
      words={{
        drop: limit > 1 ? he.upload.dropMany : he.upload.drop,
        or: he.upload.or,
        choose: he.upload.chooseFile,
        busy: he.upload.uploading,
        close: he.upload.close,
      }}
      accept={IMAGE_UPLOAD_ACCEPT}
      multiple={limit > 1}
      busy={upload.busy}
      hint={he.upload.formats}
      error={upload.error}
      onFiles={(files) => {
        void upload.start(files).then((stored) => {
          if (stored) setState('closed');
        });
      }}
      onClose={close}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}

/** One image of a form (logo, favicon, banner, popup): preview, upload, replace, remove. */
export function ImageUploadField({
  label,
  value,
  onChange,
  disabled = false,
  hint,
  error,
  testId,
}: {
  label: string;
  value: TenantMedia | null;
  onChange: (media: TenantMedia | null) => void;
  disabled?: boolean;
  hint?: string;
  error?: string | undefined;
  testId?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-slate-200 p-3">
      <legend className="px-1 text-sm font-medium text-slate-700">{label}</legend>
      <div className="flex flex-wrap items-center gap-3">
        {value === null ? (
          <span className="text-xs text-slate-500">{he.upload.none}</span>
        ) : (
          <Thumbnail file={value.file} alt={`${he.upload.preview}: ${label}`} size={80} />
        )}
        {disabled ? null : (
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <ImageAdder
              label={value === null ? he.upload.choose : he.upload.replace}
              onUploaded={onChange}
              {...(testId === undefined ? {} : { testId })}
            />
            {value === null ? null : (
              <div>
                <Button
                  tone="secondary"
                  onClick={() => {
                    onChange(null);
                  }}
                >
                  {he.upload.remove}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {hint === undefined ? null : <p className="text-xs text-slate-500">{hint}</p>}
      {error === undefined ? null : (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/**
 * The outcome of a save: the API's message (with the "changed by someone else" text for a stale
 * version), or the local "fix the fields" note when the browser already found mistakes.
 */
export const FormStatus = createFormStatus(errorMessage, he.common);

export { mergeErrors };
